import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TicketPercent, FileSpreadsheet, Loader2, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { errorText } from './SimpleModal';
import { downloadFile, todayStamp } from '../lib/download';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const when = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—');
const SCOPE = { editoras: 'Editoras (plano anual)', leitores: 'Leitores (todas as mensalidades)', ambos: 'Editoras e leitores' };
const box = { background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.25rem', marginBottom: '1.25rem' };
const inp = { height: 38, border: '1px solid #cbd5e0', borderRadius: 8, padding: '0 0.6rem', fontSize: '0.9rem', background: 'white' };
const th = { textAlign: 'left', padding: '0.5rem', fontSize: '0.78rem', color: '#718096', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
const td = { padding: '0.5rem', fontSize: '0.85rem', borderBottom: '1px solid #f1f5f9' };

const NewCoupon = ({ onCreated }) => {
  const [f, setF] = useState({ code: '', vendedor: '', percent: '', scope: 'ambos' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: k === 'code' ? e.target.value.toUpperCase() : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/admin/coupons`, { ...f, percent: Number(f.percent) }, { headers: auth() });
      toast.success('Cupom criado');
      setF({ code: '', vendedor: '', percent: '', scope: 'ambos' });
      onCreated();
    } catch (err) { toast.error(errorText(err, 'Não foi possível criar o cupom')); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="coupon-create-form" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <label style={{ fontSize: '0.78rem', color: '#4a5568' }}>Código<br /><input value={f.code} onChange={set('code')} required minLength={3} maxLength={30} placeholder="JOAO10" style={{ ...inp, width: 140, textTransform: 'uppercase' }} data-testid="coupon-code-input" /></label>
      <label style={{ fontSize: '0.78rem', color: '#4a5568', flex: '1 1 200px' }}>Vendedor vinculado<br /><input value={f.vendedor} onChange={set('vendedor')} required minLength={2} placeholder="Nome do vendedor" style={{ ...inp, width: '100%' }} data-testid="coupon-seller-input" /></label>
      <label style={{ fontSize: '0.78rem', color: '#4a5568' }}>Desconto (%)<br /><input type="number" min="1" max="90" step="0.5" value={f.percent} onChange={set('percent')} required style={{ ...inp, width: 100 }} data-testid="coupon-percent-input" /></label>
      <label style={{ fontSize: '0.78rem', color: '#4a5568' }}>Vale para<br />
        <select value={f.scope} onChange={set('scope')} style={inp} data-testid="coupon-scope-select">
          {Object.entries(SCOPE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <Button type="submit" disabled={busy} style={{ background: '#0d9488', height: 38 }} data-testid="coupon-create-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Criar cupom</>}</Button>
    </form>
  );
};

const CouponList = ({ coupons, onToggle }) => (
  <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="coupons-table">
      <thead><tr>{['Código', 'Vendedor', 'Desconto', 'Vale para', 'Usos pagos', 'Status', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
      <tbody>
        {!coupons.length ? <tr><td colSpan={7} style={{ ...td, color: '#a0aec0', textAlign: 'center' }}>Nenhum cupom criado.</td></tr> : coupons.map((c) => (
          <tr key={c.id} data-testid={`coupon-row-${c.id}`}>
            <td style={{ ...td, fontWeight: 700, fontFamily: 'monospace' }}>{c.code}</td>
            <td style={td}>{c.vendedor}</td>
            <td style={td}>{c.percent}%</td>
            <td style={td}>{SCOPE[c.scope]}</td>
            <td style={td}>{c.uses}</td>
            <td style={td}><span style={{ color: c.active ? '#2f855a' : '#a0aec0', fontWeight: 600 }} data-testid={`coupon-status-${c.id}`}>{c.active ? 'Ativo' : 'Desativado'}</span></td>
            <td style={td}><Button size="sm" variant="outline" onClick={() => onToggle(c)} data-testid={`coupon-toggle-${c.id}`}>{c.active ? 'Desativar' : 'Reativar'}</Button></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const UsesReport = ({ uses }) => {
  const [busy, setBusy] = useState(false);
  const exportXlsx = async () => {
    setBusy(true);
    try { await downloadFile(`${API}/admin/coupon-uses/export`, auth(), `cupons-${todayStamp()}.xlsx`); } catch (e) { toast.error('Não foi possível exportar'); } finally { setBusy(false); }
  };
  return (
    <div style={box} data-testid="coupon-uses-report">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <h3 style={{ fontWeight: 600 }}>Pagamentos com cupom</h3>
        <Button variant="outline" onClick={exportXlsx} disabled={busy} data-testid="coupon-uses-export-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSpreadsheet className="w-4 h-4 mr-2" style={{ color: '#217346' }} />}Exportar planilha
        </Button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="coupon-uses-table">
          <thead><tr>{['Data e hora', 'Pagador', 'Tipo', 'Modo de pagamento', 'Cupom', 'Vendedor', 'Valor pago'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {!uses.length ? <tr><td colSpan={7} style={{ ...td, color: '#a0aec0', textAlign: 'center' }}>Nenhum pagamento com cupom ainda.</td></tr> : uses.map((u) => (
              <tr key={u.id} data-testid={`coupon-use-row-${u.id}`}>
                <td style={td}>{when(u.paid_at)}</td>
                <td style={td}>{u.payer_name}<div style={{ fontSize: '0.75rem', color: '#a0aec0' }}>{u.payer_email}</div></td>
                <td style={td}>{u.use === 'editora' ? 'Editora' : 'Leitor'}</td>
                <td style={td}>{u.method}</td>
                <td style={{ ...td, fontFamily: 'monospace' }}>{u.code} ({u.percent}%)</td>
                <td style={td}>{u.vendedor}</td>
                <td style={td}>{brl(u.final_amount)} <span style={{ fontSize: '0.75rem', color: '#a0aec0' }}>de {brl(u.original_amount)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AdminCoupons = () => {
  const [coupons, setCoupons] = useState(null);
  const [uses, setUses] = useState([]);
  const load = () => Promise.all([
    axios.get(`${API}/admin/coupons`, { headers: auth() }).then((r) => setCoupons(r.data.coupons)),
    axios.get(`${API}/admin/coupon-uses`, { headers: auth() }).then((r) => setUses(r.data.uses)),
  ]).catch(() => toast.error('Erro ao carregar cupons'));
  useEffect(() => { load(); }, []);
  const toggle = async (c) => {
    try { await axios.put(`${API}/admin/coupons/${c.id}`, { active: !c.active }, { headers: auth() }); load(); } catch (e) { toast.error(errorText(e, 'Erro ao atualizar')); }
  };
  if (!coupons) return <Loader2 className="w-6 h-6 animate-spin" />;
  return (
    <div data-testid="admin-coupons">
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#2D3748', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: 10 }}><TicketPercent className="w-7 h-7" />Cupons</h1>
      <div style={box}>
        <h3 style={{ fontWeight: 600, marginBottom: 6 }}>Novo cupom</h3>
        <p style={{ fontSize: '0.8rem', color: '#718096', marginBottom: 10 }}>Para leitores o desconto vale em todas as mensalidades enquanto a assinatura estiver ativa (até o cancelamento). Cada cupom pode ser usado 1 vez por CPF/CNPJ e fica ativo até você desativar.</p>
        <NewCoupon onCreated={load} />
        <CouponList coupons={coupons} onToggle={toggle} />
      </div>
      <UsesReport uses={uses} />
    </div>
  );
};
